import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Card, Grid, Heading, HStack, Icon, Separator, SimpleGrid, Text, Textarea, VStack, chakra } from '@chakra-ui/react';
import { UserChip } from '@src/components/sco/Account/UserChip';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { SessionRatingModal } from '@src/components/sco/Advisors/modals/SessionRatingModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SectionHeader } from '@src/components/sco/Advisors/shared/SectionHeader';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { SessionDeviceCheck } from '@src/components/sco/Advisors/shared/SessionDeviceCheckCard';
import { SuggestedTimesFields, useSuggestedTimes } from '@src/components/sco/Advisors/shared/SuggestedTimesFields';
import { cleanErrorMessage, downloadTextFile, formatHourlyRate, formatUsd, formatWhen, formatWhenParts, personName, sessionMediaUrl } from '@src/components/sco/Advisors/utils/sessionFormat';
import { isJoinableSession, isNegotiatingSession, isScheduledSession, isTerminalSession, isWaitingOnCurrentUser, SESSION_STATUS, sessionStatusPalette } from '@src/components/sco/Advisors/utils/sessionStatus';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { CANCEL_ADVISOR_SESSION } from '@src/gql/mutations/CancelAdvisorSession';
import { CONFIRM_ADVISOR_SESSION_SCHEDULE } from '@src/gql/mutations/ConfirmAdvisorSessionSchedule';
import { PROPOSE_ADVISOR_SESSION_SCHEDULE } from '@src/gql/mutations/ProposeAdvisorSessionSchedule';
import { ADVISOR_SESSION, ADVISOR_SESSION_MEDIA } from '@src/gql/queries/AdvisorSession';
import { ScoAdvisorSession, ScoUser } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { LuArrowLeft, LuCalendarClock, LuCalendarPlus, LuCheck, LuClock, LuFileText, LuStar, LuVideo, LuX } from 'react-icons/lu';
type SessionQueryData = {
  advisorSession?: ScoAdvisorSession | null;
};

/** Completed-session next actions live in SessionDetailView, not the room. */

/** `recording` / `transcript` are JSON scalars on ScoAdvisorSession. */
type SessionMedia = {
  recording?: {
    url?: string;
    expiresAt?: string | null;
  } | null;
  transcript?: {
    url?: string;
  } | null;
  recordingPartyExpiresAt?: string | null;
};
type ExpertReceipt = {
  gross: number;
  platformFee: number;
  expertShare: number;
  feePercent: number;
  expertPercent: number;
};
type SessionDetailViewProps = {
  session: ScoAdvisorSession;
  media?: SessionMedia;
  advisorSessionId: string;
  sessionsHref: string;
  cancelReason: string;
  actionError: string | null;
  confirmLoading: boolean;
  proposeLoading: boolean;
  cancelLoading: boolean;
  onCancelReasonChange: (value: string) => void;
  onConfirm: (scheduledAt: string) => void;
  onPropose: (suggestedTimes: string[]) => void;
  onCancel: () => void;
  onRate: () => void;
  bookAgainHref?: string | null;
  opportunitiesHref: string;
  sessionSettingsHref: string;
  inboxHref?: string | null;
  canInviteToBoard?: boolean;
  onInviteToBoard?: () => void;
};
const toChipUser = (user: ScoAdvisorSession['expertUser'], fallback: string): ScoUser => ({
  ...(user ?? {}),
  id: user?.id || user?.userId || fallback,
  userId: user?.userId,
  displayName: personName(user, fallback),
  fullName: personName(user, fallback),
  pictureUrl: user?.pictureUrl
}) as ScoUser;
const DetailCard = t0 => {
  const $ = _c(10);
  const {
    label,
    description,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/55" textStyle="labelXs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <Card.Root boxShadow="sm" variant="subtle"><Card.Body gap={4}>{t3}{children}</Card.Body></Card.Root>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const MetaItem = t0 => {
  const $ = _c(7);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/50" textStyle="label2xs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <Box color="colorPalette.fg/90" fontFamily="longform" fontSize="sm" fontWeight="medium">{children}</Box>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1} minWidth={0}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const ReceiptRow = t0 => {
  const $ = _c(12);
  const {
    label,
    value,
    emphasize
  } = t0;
  const t1 = emphasize ? "colorPalette.fg" : "colorPalette.fg/55";
  const t2 = emphasize ? "sm" : "xs";
  const t3 = emphasize ? "semibold" : "medium";
  let t4;
  if ($[0] !== label || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    t4 = <Text color={t1} fontSize={t2} fontWeight={t3}>{label}</Text>;
    $[0] = label;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = emphasize ? "lg" : "sm";
  const t6 = emphasize ? "semibold" : "medium";
  let t7;
  if ($[5] !== t5 || $[6] !== t6 || $[7] !== value) {
    t7 = <Text fontFamily="longform" fontSize={t5} fontWeight={t6} textAlign="end">{value}</Text>;
    $[5] = t5;
    $[6] = t6;
    $[7] = value;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <HStack align="baseline" gap={4} justify="space-between">{t4}{t7}</HStack>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};
const PersonTile = t0 => {
  const $ = _c(16);
  const {
    label,
    user,
    fallback,
    isYou
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/50" textStyle="label2xs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isYou) {
    t2 = isYou && <Badge size="sm" variant="subtle">You</Badge>;
    $[2] = isYou;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <HStack justify="space-between">{t1}{t2}</HStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== fallback || $[8] !== user) {
    t4 = toChipUser(user, fallback);
    $[7] = fallback;
    $[8] = user;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      disablePresence: true,
      avatarProps: {
        size: "md"
      }
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4) {
    t6 = <UserChip user={t4} userAvatarProps={t5} />;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <VStack align="stretch" bg="colorPalette.card/50" borderColor="colorPalette.border/60" borderRadius="l2" borderWidth="1px" gap={3} minWidth={0} padding={3}>{t3}{t6}</VStack>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
type ProposeTimesFormProps = {
  session: ScoAdvisorSession;
  canConfirm: boolean;
  waitingOnCounterpart: boolean;
  counterpartLabel: string;
  loading: boolean;
  onPropose: (suggestedTimes: string[]) => void;
};
const proposeTimesDescription = ({
  canConfirm,
  waitingOnCounterpart,
  counterpartLabel
}: Pick<ProposeTimesFormProps, 'canConfirm' | 'waitingOnCounterpart' | 'counterpartLabel'>) => {
  if (canConfirm) {
    return 'Offer up to three alternatives instead of accepting a proposed slot.';
  }
  if (waitingOnCounterpart) {
    return `You can revise your availability; the ${counterpartLabel} will need to accept.`;
  }
  return 'Suggest up to three times that work for you.';
};

/**
 * Propose-times fields. Mounted only once the session has loaded so the inputs
 * can seed straight from server data — no hydration flag, no effect.
 */
const ProposeTimesForm = t0 => {
  const $ = _c(22);
  const {
    session,
    canConfirm,
    waitingOnCounterpart,
    counterpartLabel,
    loading,
    onPropose
  } = t0;
  let t1;
  if ($[0] !== session.suggestedTime1 || $[1] !== session.suggestedTime2 || $[2] !== session.suggestedTime3) {
    t1 = [session.suggestedTime1, session.suggestedTime2, session.suggestedTime3];
    $[0] = session.suggestedTime1;
    $[1] = session.suggestedTime2;
    $[2] = session.suggestedTime3;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const times = useSuggestedTimes(t1);
  let t2;
  if ($[4] !== canConfirm || $[5] !== counterpartLabel || $[6] !== waitingOnCounterpart) {
    t2 = proposeTimesDescription({
      canConfirm,
      waitingOnCounterpart,
      counterpartLabel
    });
    $[4] = canConfirm;
    $[5] = counterpartLabel;
    $[6] = waitingOnCounterpart;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const description = t2;
  let t3;
  if ($[8] !== description) {
    t3 = <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text>;
    $[8] = description;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== times) {
    t4 = <SuggestedTimesFields label="Your proposed times" times={times} />;
    $[10] = times;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onPropose || $[13] !== times.isoValues) {
    t5 = () => onPropose(times.isoValues);
    $[12] = onPropose;
    $[13] = times.isoValues;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== loading || $[16] !== t5) {
    t6 = <Button alignSelf="flex-start" loading={loading} variant="outline" onClick={t5}>Send proposed times</Button>;
    $[15] = loading;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t6) {
    t7 = <VStack align="stretch" gap={4}>{t3}{t4}{t6}</VStack>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
const ProposeTimesCard = props => {
  const $ = _c(5);
  const t0 = props.canConfirm ? "Propose different times" : "Propose new times";
  let t1;
  if ($[0] !== props) {
    t1 = <ProposeTimesForm {...props} />;
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <DetailCard label={t0}>{t1}</DetailCard>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const SessionDetailView = t0 => {
  const $ = _c(189);
  const {
    session,
    media,
    advisorSessionId,
    sessionsHref,
    cancelReason,
    actionError,
    confirmLoading,
    proposeLoading,
    cancelLoading,
    onCancelReasonChange,
    onConfirm,
    onPropose,
    onCancel,
    onRate,
    bookAgainHref,
    opportunitiesHref,
    sessionSettingsHref,
    inboxHref,
    canInviteToBoard,
    onInviteToBoard
  } = t0;
  const statusKey = session.statusKey;
  const role = session.currentUserRole;
  let t1;
  if ($[0] !== statusKey) {
    t1 = isTerminalSession(statusKey);
    $[0] = statusKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isTerminal = t1;
  let t2;
  if ($[2] !== statusKey) {
    t2 = isNegotiatingSession(statusKey);
    $[2] = statusKey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isNegotiating = t2;
  let t3;
  if ($[4] !== statusKey) {
    t3 = isScheduledSession(statusKey);
    $[4] = statusKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isScheduled = t3;
  const isCompleted = statusKey === SESSION_STATUS.completed;
  let t4;
  if ($[6] !== role || $[7] !== statusKey) {
    t4 = isWaitingOnCurrentUser(statusKey, role);
    $[6] = role;
    $[7] = statusKey;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const canConfirm = t4;
  const waitingOnCounterpart = isNegotiating && !canConfirm;
  const counterpartLabel = role === "EXPERT" ? "founder" : "advisor";
  const canRate = !!session.canRate;
  const myRating = session.currentUserRating;
  const ics = session.ics as {
    fileName?: string;
    content?: string;
  } | null | undefined;
  const t5 = media?.recording;
  let t6;
  if ($[9] !== t5) {
    t6 = sessionMediaUrl(t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const recordingUrl = t6;
  const t7 = media?.transcript;
  let t8;
  if ($[11] !== t7) {
    t8 = sessionMediaUrl(t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const transcriptUrl = t8;
  media?.recording && typeof media.recording === "object" ? media.recording.expiresAt : null;
  let t9;
  if ($[13] !== session.scheduledAt) {
    t9 = formatWhenParts(session.scheduledAt);
    $[13] = session.scheduledAt;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const scheduledParts = t9;
  const durationLabel = session.approximateLengthMinutes != null ? `${session.approximateLengthMinutes} min` : "\u2014";
  const [proposeOpen, setProposeOpen] = useState(false);
  let t10;
  if ($[15] !== canConfirm || $[16] !== counterpartLabel || $[17] !== onPropose || $[18] !== proposeLoading || $[19] !== session || $[20] !== waitingOnCounterpart) {
    t10 = {
      canConfirm,
      counterpartLabel,
      loading: proposeLoading,
      session,
      waitingOnCounterpart,
      onPropose
    };
    $[15] = canConfirm;
    $[16] = counterpartLabel;
    $[17] = onPropose;
    $[18] = proposeLoading;
    $[19] = session;
    $[20] = waitingOnCounterpart;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const proposeTimesProps = t10;
  let t11;
  if ($[22] !== session.suggestedTime1 || $[23] !== session.suggestedTime2 || $[24] !== session.suggestedTime3) {
    t11 = [session.suggestedTime1, session.suggestedTime2, session.suggestedTime3].filter(_temp);
    $[22] = session.suggestedTime1;
    $[23] = session.suggestedTime2;
    $[24] = session.suggestedTime3;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const suggestedTimes = t11;
  let t12;
  bb0: {
    if (session.finalCostUsd == null) {
      t12 = null;
      break bb0;
    }
    const gross = Number(session.finalCostUsd);
    const feePercent = Number(session.platformFeePercent ?? 15);
    const platformFee = session.platformFeeAmount != null ? Number(session.platformFeeAmount) : Math.round(gross * (feePercent / 100) * 100) / 100;
    const expertShare = session.expertShareAmount != null ? Number(session.expertShareAmount) : Math.round((gross - platformFee) * 100) / 100;
    const t13 = 100 - feePercent;
    let t14;
    if ($[26] !== expertShare || $[27] !== feePercent || $[28] !== gross || $[29] !== platformFee || $[30] !== t13) {
      t14 = {
        gross,
        platformFee,
        expertShare,
        feePercent,
        expertPercent: t13
      };
      $[26] = expertShare;
      $[27] = feePercent;
      $[28] = gross;
      $[29] = platformFee;
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    t12 = t14;
  }
  const expertReceipt = t12;
  const proposedTimesDescription = canConfirm ? "Accept a slot, or propose different times below." : waitingOnCounterpart ? `Waiting for the ${counterpartLabel} to accept one of these times (or propose alternatives). Only they can accept a slot from this proposal.` : undefined;
  const cancelDescription = canConfirm && role === "EXPERT" ? "Decline this session request. The founder\u2019s card hold will be released." : session.statusKey === "schedule_confirmed" || session.statusKey === "live" ? "Cancel before anyone joins for a full refund. After someone joins, the package is kept." : "Cancel this session. Any authorized card hold will be released.";
  const cancelLabel = canConfirm && role === "EXPERT" ? "Decline request" : "Cancel session";
  let t13;
  if ($[32] !== advisorSessionId || $[33] !== ics) {
    t13 = () => {
      if (!ics?.content) {
        return;
      }
      downloadTextFile(ics.fileName || `advisor-session-${advisorSessionId}.ics`, ics.content, "text/calendar;charset=utf-8");
    };
    $[32] = advisorSessionId;
    $[33] = ics;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const handleDownloadIcs = t13;
  let t14;
  if ($[35] !== durationLabel) {
    t14 = <MetaItem label="Duration">{durationLabel}</MetaItem>;
    $[35] = durationLabel;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== session.hourlyRate) {
    t15 = formatHourlyRate(session.hourlyRate) || "\u2014";
    $[37] = session.hourlyRate;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <MetaItem label="Rate">{t15}</MetaItem>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== session.requestedAt) {
    t17 = formatWhen(session.requestedAt);
    $[41] = session.requestedAt;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <MetaItem label="Requested">{t17}</MetaItem>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== session.estimatedCostUsd) {
    t19 = session.estimatedCostUsd != null ? formatUsd(session.estimatedCostUsd) : "\u2014";
    $[45] = session.estimatedCostUsd;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19) {
    t20 = <MetaItem label="Estimated">{t19}</MetaItem>;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t14 || $[50] !== t16 || $[51] !== t18 || $[52] !== t20) {
    t21 = <SimpleGrid columns={2} gap={4}>{t14}{t16}{t18}{t20}</SimpleGrid>;
    $[49] = t14;
    $[50] = t16;
    $[51] = t18;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== session.cancelationReason) {
    t22 = session.cancelationReason && <><Separator /><MetaItem label="Cancel reason">{session.cancelationReason}</MetaItem></>;
    $[54] = session.cancelationReason;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t21 || $[57] !== t22) {
    t23 = <DetailCard label="Details">{t21}{t22}</DetailCard>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const detailsCard = t23;
  let t24;
  if ($[59] !== isScheduled || $[60] !== proposeOpen) {
    t24 = isScheduled && <Button aria-expanded={proposeOpen} flexShrink={0} size="sm" variant={proposeOpen ? "subtle" : "outline"} onClick={() => setProposeOpen(_temp2)}><LuCalendarClock /> Reschedule</Button>;
    $[59] = isScheduled;
    $[60] = proposeOpen;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== handleDownloadIcs || $[63] !== ics?.content) {
    t25 = ics?.content && <Button flexShrink={0} size="sm" variant="outline" onClick={handleDownloadIcs}><LuCalendarPlus /> Add to calendar</Button>;
    $[62] = handleDownloadIcs;
    $[63] = ics?.content;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t24 || $[66] !== t25) {
    t26 = <HStack flexShrink={0} gap={2} wrap="wrap">{t24}{t25}</HStack>;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  const whenActions = t26;
  let t27;
  if ($[68] !== counterpartLabel || $[69] !== durationLabel || $[70] !== scheduledParts || $[71] !== suggestedTimes || $[72] !== waitingOnCounterpart) {
    t27 = <VStack align="stretch" gap={1} minWidth={0}>{scheduledParts ? <><Heading size="xl">{scheduledParts.date}</Heading><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="md">{scheduledParts.time} · {durationLabel}</Text></> : <><Heading size="lg">Not confirmed yet</Heading><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{suggestedTimes.length ? `${suggestedTimes.length} proposed time${suggestedTimes.length === 1 ? "" : "s"} below` : waitingOnCounterpart ? `Waiting for the ${counterpartLabel} to pick a time.` : "Propose times to get this on the calendar."}</Text></>}</VStack>;
    $[68] = counterpartLabel;
    $[69] = durationLabel;
    $[70] = scheduledParts;
    $[71] = suggestedTimes;
    $[72] = waitingOnCounterpart;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t27 || $[75] !== whenActions) {
    t28 = <HStack align="flex-start" gap={4} justify="space-between" wrap="wrap">{t27}{whenActions}</HStack>;
    $[74] = t27;
    $[75] = whenActions;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== isScheduled || $[78] !== proposeOpen || $[79] !== proposeTimesProps) {
    t29 = isScheduled && proposeOpen && <><Separator /><ProposeTimesForm {...proposeTimesProps} /></>;
    $[77] = isScheduled;
    $[78] = proposeOpen;
    $[79] = proposeTimesProps;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t28 || $[82] !== t29) {
    t30 = <DetailCard label="When">{t28}{t29}</DetailCard>;
    $[81] = t28;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  const whenCard = t30;
  let t31;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      base: 1,
      sm: 2
    };
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  const t32 = role === "EXPERT";
  let t33;
  if ($[85] !== session.expertUser || $[86] !== t32) {
    t33 = <PersonTile fallback="Advisor" isYou={t32} label="Expert" user={session.expertUser} />;
    $[85] = session.expertUser;
    $[86] = t32;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  const t34 = role === "SEEKER";
  let t35;
  if ($[88] !== session.seekerUser || $[89] !== t34) {
    t35 = <PersonTile fallback="Founder" isYou={t34} label="Founder" user={session.seekerUser} />;
    $[88] = session.seekerUser;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t33 || $[92] !== t35) {
    t36 = <DetailCard label="People"><SimpleGrid columns={t31} gap={3}>{t33}{t35}</SimpleGrid></DetailCard>;
    $[91] = t33;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== canConfirm || $[95] !== confirmLoading || $[96] !== isNegotiating || $[97] !== onConfirm || $[98] !== proposedTimesDescription || $[99] !== suggestedTimes) {
    t37 = isNegotiating && <DetailCard description={proposedTimesDescription} label={canConfirm ? "Accept a proposed time" : "Proposed times"}>{suggestedTimes.length === 0 ? <Text color="colorPalette.fg/50" fontSize="sm">No proposed times yet{canConfirm ? " \u2014 use Propose different times below." : "."}</Text> : <VStack align="stretch" gap={2}>{suggestedTimes.map(time_0 => {
          const parts = formatWhenParts(time_0);
          return <HStack key={time_0} align="center" borderColor="colorPalette.border" borderRadius="l2" borderWidth="1px" gap={3} justify="space-between" paddingX={4} paddingY={3} wrap="wrap"><VStack align="stretch" gap={0.5} minWidth={0}><Text fontWeight="semibold">{parts?.date || formatWhen(time_0)}</Text>{parts && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{parts.time}</Text>}</VStack>{canConfirm && <Button loading={confirmLoading} size="sm" variant="cta" onClick={() => onConfirm(time_0)}><LuCheck /> Accept</Button>}</HStack>;
        })}</VStack>}</DetailCard>;
    $[94] = canConfirm;
    $[95] = confirmLoading;
    $[96] = isNegotiating;
    $[97] = onConfirm;
    $[98] = proposedTimesDescription;
    $[99] = suggestedTimes;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] !== isCompleted || $[102] !== recordingUrl || $[103] !== transcriptUrl) {
    t38 = (isCompleted || recordingUrl || transcriptUrl) && <DetailCard label="Recording & transcript"><VStack align="stretch" gap={4}>{recordingUrl ? <VStack align="stretch" gap={2}><Text fontSize="sm" fontWeight="medium">Audio recording</Text><chakra.audio controls={true} src={recordingUrl} width="full" /></VStack> : <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">Recording usually lands a minute or two after the call ends. Refresh this page if it is still missing.</Text>}{transcriptUrl ? <Button asChild={true} alignSelf="flex-start" size="sm" variant="outline"><a href={transcriptUrl} rel="noreferrer" target="_blank"><LuFileText /> Open transcript</a></Button> : isCompleted ? <Text color="colorPalette.fg/50" fontSize="xs">Transcript appears here when Stream finishes processing.</Text> : null}</VStack></DetailCard>;
    $[101] = isCompleted;
    $[102] = recordingUrl;
    $[103] = transcriptUrl;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  let t39;
  if ($[105] !== isScheduled || $[106] !== isTerminal || $[107] !== proposeTimesProps) {
    t39 = !isTerminal && !isScheduled && <ProposeTimesCard {...proposeTimesProps} />;
    $[105] = isScheduled;
    $[106] = isTerminal;
    $[107] = proposeTimesProps;
    $[108] = t39;
  } else {
    t39 = $[108];
  }
  let t40;
  if ($[109] !== cancelDescription || $[110] !== cancelLabel || $[111] !== cancelLoading || $[112] !== cancelReason || $[113] !== isTerminal || $[114] !== onCancel || $[115] !== onCancelReasonChange) {
    t40 = !isTerminal && <DetailCard description={cancelDescription} label={cancelLabel}><Textarea placeholder="Optional reason" rows={2} value={cancelReason} onChange={event => onCancelReasonChange(event.target.value)} /><Button alignSelf="flex-start" colorPalette="red" loading={cancelLoading} variant="outline" onClick={onCancel}><LuX /> {cancelLabel}</Button></DetailCard>;
    $[109] = cancelDescription;
    $[110] = cancelLabel;
    $[111] = cancelLoading;
    $[112] = cancelReason;
    $[113] = isTerminal;
    $[114] = onCancel;
    $[115] = onCancelReasonChange;
    $[116] = t40;
  } else {
    t40 = $[116];
  }
  let t41;
  if ($[117] !== t36 || $[118] !== t37 || $[119] !== t38 || $[120] !== t39 || $[121] !== t40) {
    t41 = <>{t36}{t37}{t38}{t39}{t40}</>;
    $[117] = t36;
    $[118] = t37;
    $[119] = t38;
    $[120] = t39;
    $[121] = t40;
    $[122] = t41;
  } else {
    t41 = $[122];
  }
  const mainColumnCards = t41;
  let t42;
  if ($[123] !== expertReceipt || $[124] !== role) {
    t42 = expertReceipt && role === "EXPERT" && <DetailCard label="Your receipt"><VStack align="stretch" gap={3}><ReceiptRow label="Session" value={formatUsd(expertReceipt.gross)} /><ReceiptRow label={`Platform fee (${expertReceipt.feePercent}%)`} value={formatUsd(expertReceipt.platformFee)} /><Separator /><ReceiptRow emphasize={true} label={`You receive (${expertReceipt.expertPercent}%)`} value={formatUsd(expertReceipt.expertShare)} /></VStack></DetailCard>;
    $[123] = expertReceipt;
    $[124] = role;
    $[125] = t42;
  } else {
    t42 = $[125];
  }
  let t43;
  if ($[126] !== role || $[127] !== session.estimatedCostUsd || $[128] !== session.finalCostUsd) {
    t43 = session.finalCostUsd != null && role !== "EXPERT" && <DetailCard label="Cost"><ReceiptRow emphasize={true} label="Final cost" value={formatUsd(session.finalCostUsd)} />{session.estimatedCostUsd != null && <Text color="colorPalette.fg/50" fontSize="xs">Estimated {formatUsd(session.estimatedCostUsd)} before the call</Text>}</DetailCard>;
    $[126] = role;
    $[127] = session.estimatedCostUsd;
    $[128] = session.finalCostUsd;
    $[129] = t43;
  } else {
    t43 = $[129];
  }
  let t44;
  if ($[130] !== canRate || $[131] !== isCompleted || $[132] !== myRating || $[133] !== onRate) {
    t44 = isCompleted && canRate && <DetailCard label="Your rating">{myRating ? <VStack align="stretch" gap={3}><HStack gap={1}>{Array.from({
            length: 5
          }, (_, index) => <Icon key={index} boxSize={4} color={index < (myRating.score ?? 0) ? "yellow.fg" : "colorPalette.fg/25"} fill={index < (myRating.score ?? 0) ? "currentColor" : "none"}><LuStar /></Icon>)}<Text color="colorPalette.fg/60" fontSize="sm">{myRating.score}/5</Text></HStack>{myRating.note && <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{myRating.note}</Text>}<Button alignSelf="flex-start" size="xs" variant="outline" onClick={onRate}>Update</Button></VStack> : <Button alignSelf="flex-start" size="sm" variant="outline" onClick={onRate}><LuStar /> Rate this session</Button>}</DetailCard>;
    $[130] = canRate;
    $[131] = isCompleted;
    $[132] = myRating;
    $[133] = onRate;
    $[134] = t44;
  } else {
    t44 = $[134];
  }
  let t45;
  if ($[135] !== t42 || $[136] !== t43 || $[137] !== t44) {
    t45 = <>{t42}{t43}{t44}</>;
    $[135] = t42;
    $[136] = t43;
    $[137] = t44;
    $[138] = t45;
  } else {
    t45 = $[138];
  }
  const asideCards = t45;
  let t46;
  if ($[139] !== statusKey) {
    t46 = sessionStatusPalette(statusKey);
    $[139] = statusKey;
    $[140] = t46;
  } else {
    t46 = $[140];
  }
  const t47 = session.status?.title || statusKey;
  let t48;
  if ($[141] !== t46 || $[142] !== t47) {
    t48 = <Badge colorPalette={t46} variant="subtle">{t47}</Badge>;
    $[141] = t46;
    $[142] = t47;
    $[143] = t48;
  } else {
    t48 = $[143];
  }
  let t49;
  if ($[144] !== role) {
    t49 = role && <Badge variant="outline">You are the {role === "EXPERT" ? "expert" : "founder"}</Badge>;
    $[144] = role;
    $[145] = t49;
  } else {
    t49 = $[145];
  }
  let t50;
  if ($[146] !== canConfirm || $[147] !== isTerminal) {
    t50 = canConfirm && !isTerminal && <Badge colorPalette="yellow" variant="subtle">Waiting on you</Badge>;
    $[146] = canConfirm;
    $[147] = isTerminal;
    $[148] = t50;
  } else {
    t50 = $[148];
  }
  let t51;
  if ($[149] !== t48 || $[150] !== t49 || $[151] !== t50) {
    t51 = <HStack gap={2} wrap="wrap">{t48}{t49}{t50}</HStack>;
    $[149] = t48;
    $[150] = t49;
    $[151] = t50;
    $[152] = t51;
  } else {
    t51 = $[152];
  }
  let t52;
  if ($[153] !== bookAgainHref || $[154] !== canInviteToBoard || $[155] !== canRate || $[156] !== inboxHref || $[157] !== isCompleted || $[158] !== myRating || $[159] !== onInviteToBoard || $[160] !== onRate || $[161] !== opportunitiesHref || $[162] !== role || $[163] !== sessionSettingsHref) {
    t52 = isCompleted && <Card.Root variant="subtle"><Card.Body gap={3}><Text fontWeight="semibold">This session is complete</Text><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{canRate && !myRating ? "Rate the call, then pick a next step. A paid session is not a board seat." : role === "EXPERT" ? "Next: more opportunities, or update the rates founders see when they book you." : "Book another paid hour, or invite this advisor to a board seat \u2014 those are separate paths."}</Text><HStack gap={2} wrap="wrap">{canRate && !myRating ? <Button size="sm" variant="cta" onClick={onRate}><LuStar /> Rate this session</Button> : null}{role === "SEEKER" && bookAgainHref ? <Button asChild={true} size="sm" variant={canRate && !myRating ? "outline" : "cta"}><NextLink href={bookAgainHref}>Book again</NextLink></Button> : null}{role === "SEEKER" && canInviteToBoard && onInviteToBoard ? <PaywalledAdvisorAction intent="advisor-invite" upgradeCtaButtonProps={{
            size: "sm"
          }} upgradeCtaLabel="Upgrade to invite"><Button size="sm" variant="outline" onClick={onInviteToBoard}>Invite to board</Button></PaywalledAdvisorAction> : null}{role === "SEEKER" && inboxHref ? <Button asChild={true} size="sm" variant="ghost"><NextLink href={inboxHref}>Open Inbox</NextLink></Button> : null}{role === "EXPERT" ? <><Button asChild={true} size="sm" variant="cta"><NextLink href={opportunitiesHref}>View opportunities</NextLink></Button><Button asChild={true} size="sm" variant="outline"><NextLink href={sessionSettingsHref}>Session rates</NextLink></Button></> : null}</HStack></Card.Body></Card.Root>;
    $[153] = bookAgainHref;
    $[154] = canInviteToBoard;
    $[155] = canRate;
    $[156] = inboxHref;
    $[157] = isCompleted;
    $[158] = myRating;
    $[159] = onInviteToBoard;
    $[160] = onRate;
    $[161] = opportunitiesHref;
    $[162] = role;
    $[163] = sessionSettingsHref;
    $[164] = t52;
  } else {
    t52 = $[164];
  }
  let t53;
  if ($[165] !== canConfirm || $[166] !== isTerminal || $[167] !== role) {
    t53 = canConfirm && !isTerminal && <Card.Root colorPalette="yellow" variant="subtle"><Card.Body><HStack align="flex-start" gap={3}><Icon boxSize={5} color="yellow.fg" marginTop={0.5}><LuClock /></Icon><VStack align="stretch" gap={1}><Text fontWeight="semibold">Action required</Text><Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm">{role === "EXPERT" ? "The founder suggested times below. Accept one, propose different times, or decline." : "The advisor suggested times below. Accept one, propose different times, or cancel."}</Text></VStack></HStack></Card.Body></Card.Root>;
    $[165] = canConfirm;
    $[166] = isTerminal;
    $[167] = role;
    $[168] = t53;
  } else {
    t53 = $[168];
  }
  let t54;
  if ($[169] !== actionError) {
    t54 = actionError && <Text color="red.fg" fontFamily="longform" fontSize="sm">{actionError}</Text>;
    $[169] = actionError;
    $[170] = t54;
  } else {
    t54 = $[170];
  }
  let t55;
  if ($[171] !== asideCards || $[172] !== detailsCard || $[173] !== isNegotiating || $[174] !== isScheduled || $[175] !== mainColumnCards || $[176] !== whenCard) {
    t55 = isNegotiating ? <VStack align="stretch" gap={5}>{detailsCard}{whenCard}{mainColumnCards}</VStack> : <Grid alignItems="start" gap={5} templateColumns={{
      base: "1fr",
      lg: "minmax(0, 1.45fr) minmax(260px, 0.9fr)"
    }}><VStack align="stretch" gap={5}>{whenCard}{mainColumnCards}</VStack><VStack align="stretch" gap={5}>{detailsCard}{isScheduled && <SessionDeviceCheck />}{asideCards}</VStack></Grid>;
    $[171] = asideCards;
    $[172] = detailsCard;
    $[173] = isNegotiating;
    $[174] = isScheduled;
    $[175] = mainColumnCards;
    $[176] = whenCard;
    $[177] = t55;
  } else {
    t55 = $[177];
  }
  let t56;
  if ($[178] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <Separator />;
    $[178] = t56;
  } else {
    t56 = $[178];
  }
  let t57;
  if ($[179] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <LuArrowLeft />;
    $[179] = t57;
  } else {
    t57 = $[179];
  }
  let t58;
  if ($[180] !== sessionsHref) {
    t58 = <Button asChild={true} alignSelf="flex-start" variant="ghost"><NextLink href={sessionsHref}>{t57} All sessions</NextLink></Button>;
    $[180] = sessionsHref;
    $[181] = t58;
  } else {
    t58 = $[181];
  }
  let t59;
  if ($[182] !== t51 || $[183] !== t52 || $[184] !== t53 || $[185] !== t54 || $[186] !== t55 || $[187] !== t58) {
    t59 = <VStack align="stretch" gap={5}>{t51}{t52}{t53}{t54}{t55}{t56}{t58}</VStack>;
    $[182] = t51;
    $[183] = t52;
    $[184] = t53;
    $[185] = t54;
    $[186] = t55;
    $[187] = t58;
    $[188] = t59;
  } else {
    t59 = $[188];
  }
  return t59;
};
export const SessionDetailSection = () => {
  const $ = _c(121);
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const t0 = String(router.query.advisorSessionId || "");
  let t1;
  if ($[0] !== t0) {
    t1 = t0.trim();
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const advisorSessionId = t1;
  const [cancelReason, setCancelReason] = useState("");
  const [actionError, setActionError] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  let t2;
  if ($[2] !== advisorSessionId || $[3] !== paths.sessions) {
    t2 = advisorsBreadcrumbsWithTrail({
      title: "My sessions",
      href: paths.sessions
    }, {
      title: "Session",
      href: advisorSessionId ? `${paths.sessions}/${advisorSessionId}` : paths.sessions
    });
    $[2] = advisorSessionId;
    $[3] = paths.sessions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      breadcrumbs: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useAdvisorsToolbar(t3);
  let t4;
  if ($[7] !== advisorSessionId) {
    t4 = {
      advisorSessionId
    };
    $[7] = advisorSessionId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !router.isReady || !advisorSessionId;
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = {
      variables: t4,
      skip: t5,
      fetchPolicy: "network-only"
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const query = useQuery(ADVISOR_SESSION, t6);
  const session = query.data?.advisorSession;
  const isCompleted = session?.statusKey === SESSION_STATUS.completed;
  let t7;
  if ($[12] !== advisorSessionId) {
    t7 = {
      advisorSessionId
    };
    $[12] = advisorSessionId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = !advisorSessionId;
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = {
      variables: t7,
      skip: t8,
      errorPolicy: "all",
      fetchPolicy: "cache-and-network"
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const mediaQuery = useQuery(ADVISOR_SESSION_MEDIA, t9);
  const media = mediaQuery.data?.advisorSession as SessionMedia | undefined;
  let t10;
  if ($[17] !== media?.recording || $[18] !== media?.transcript) {
    t10 = sessionMediaUrl(media?.recording) || sessionMediaUrl(media?.transcript);
    $[17] = media?.recording;
    $[18] = media?.transcript;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const hasSessionMedia = !!t10;
  let t11;
  if ($[20] !== advisorSessionId || $[21] !== hasSessionMedia || $[22] !== isCompleted || $[23] !== mediaQuery) {
    t11 = () => {
      if (!isCompleted || hasSessionMedia || !advisorSessionId) {
        return;
      }
      const timer = window.setInterval(() => {
        mediaQuery.refetch();
      }, 8000);
      return () => window.clearInterval(timer);
    };
    $[20] = advisorSessionId;
    $[21] = hasSessionMedia;
    $[22] = isCompleted;
    $[23] = mediaQuery;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== advisorSessionId || $[26] !== hasSessionMedia || $[27] !== isCompleted || $[28] !== mediaQuery.refetch) {
    t12 = [advisorSessionId, hasSessionMedia, isCompleted, mediaQuery.refetch];
    $[25] = advisorSessionId;
    $[26] = hasSessionMedia;
    $[27] = isCompleted;
    $[28] = mediaQuery.refetch;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  useEffect(t11, t12);
  const role = session?.currentUserRole;
  const canJoin = isJoinableSession(session?.statusKey);
  const canRate = !!session?.canRate;
  const myRating = session?.currentUserRating;
  const counterpartLabel = role === "EXPERT" ? "founder" : "advisor";
  const t13 = role === "EXPERT" ? session?.seekerUser : session?.expertUser;
  const t14 = counterpartLabel === "founder" ? "Founder" : "Advisor";
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = personName(t13, t14);
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const counterpartName = t15;
  const roomHref = `${paths.sessions}/${advisorSessionId}/room`;
  const expertUserId = session?.expertUserId || session?.expertUser?.userId;
  const canBookAgain = role === "SEEKER" && !!session?.advisorProfile?.canAcceptSessions && !!expertUserId;
  let t16;
  if ($[33] !== canBookAgain || $[34] !== expertUserId || $[35] !== paths) {
    t16 = canBookAgain ? paths.withQuery(paths.sessionNew, {
      expertUserId
    }) : null;
    $[33] = canBookAgain;
    $[34] = expertUserId;
    $[35] = paths;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const bookAgainHref = t16;
  const hasBoardSeat = !!session?.boardEngagement?.advisorEngagementId;
  const canInviteToBoard = role === "SEEKER" && !!expertUserId && !hasBoardSeat;
  let t17;
  if ($[37] !== expertUserId || $[38] !== hasBoardSeat || $[39] !== paths) {
    t17 = hasBoardSeat ? paths.withQuery(paths.inbox, {
      advisorUserId: expertUserId
    }) : null;
    $[37] = expertUserId;
    $[38] = hasBoardSeat;
    $[39] = paths;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const inboxHref = t17;
  let t18;
  let t19;
  if ($[41] !== advisorSessionId || $[42] !== canRate || $[43] !== isCompleted || $[44] !== myRating || $[45] !== router.isReady || $[46] !== router.query.rate || $[47] !== session) {
    t18 = () => {
      if (!router.isReady || !session || !advisorSessionId) {
        return;
      }
      if (!isCompleted || !canRate || myRating) {
        return;
      }
      const wantRate = String(router.query.rate || "") === "1";
      const storageKey = `sco.advisors.sessionRatePrompted.${advisorSessionId}`;
      const alreadyPrompted = typeof window !== "undefined" && window.sessionStorage.getItem(storageKey) === "1";
      if (!wantRate && alreadyPrompted) {
        return;
      }
      setRateOpen(true);
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(storageKey, "1");
      }
    };
    t19 = [advisorSessionId, canRate, isCompleted, myRating, router.isReady, router.query.rate, session];
    $[41] = advisorSessionId;
    $[42] = canRate;
    $[43] = isCompleted;
    $[44] = myRating;
    $[45] = router.isReady;
    $[46] = router.query.rate;
    $[47] = session;
    $[48] = t18;
    $[49] = t19;
  } else {
    t18 = $[48];
    t19 = $[49];
  }
  useEffect(t18, t19);
  let t20;
  if ($[50] !== advisorSessionId) {
    t20 = [{
      query: ADVISOR_SESSION,
      variables: {
        advisorSessionId
      }
    }];
    $[50] = advisorSessionId;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const refetchSession = t20;
  let t21;
  if ($[52] !== refetchSession) {
    t21 = {
      refetchQueries: refetchSession
    };
    $[52] = refetchSession;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  const [confirmSchedule, confirmState] = useMutation(CONFIRM_ADVISOR_SESSION_SCHEDULE, t21);
  let t22;
  if ($[54] !== refetchSession) {
    t22 = {
      refetchQueries: refetchSession
    };
    $[54] = refetchSession;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const [proposeSchedule, proposeState] = useMutation(PROPOSE_ADVISOR_SESSION_SCHEDULE, t22);
  let t23;
  if ($[56] !== refetchSession) {
    t23 = {
      refetchQueries: refetchSession
    };
    $[56] = refetchSession;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const [cancelSession, cancelState] = useMutation(CANCEL_ADVISOR_SESSION, t23);
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = async (fn, fallback) => {
      setActionError(null);
      ;
      try {
        await fn();
      } catch (t25) {
        const err = t25;
        setActionError(cleanErrorMessage(err, fallback));
      }
    };
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const run = t24;
  let t25;
  if ($[59] !== advisorSessionId || $[60] !== confirmSchedule) {
    t25 = scheduledAt => run(() => confirmSchedule({
      variables: {
        input: {
          advisorSessionId,
          scheduledAt
        }
      }
    }), "Could not confirm that time.");
    $[59] = advisorSessionId;
    $[60] = confirmSchedule;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  const handleConfirm = t25;
  let t26;
  if ($[62] !== advisorSessionId || $[63] !== proposeSchedule) {
    t26 = suggestedTimesIso => {
      if (!suggestedTimesIso.length) {
        setActionError("Pick at least one suggested time.");
        return;
      }
      run(() => proposeSchedule({
        variables: {
          input: {
            advisorSessionId,
            suggestedTimes: suggestedTimesIso
          }
        }
      }), "Could not send those times.");
    };
    $[62] = advisorSessionId;
    $[63] = proposeSchedule;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  const handlePropose = t26;
  let t27;
  if ($[65] !== advisorSessionId || $[66] !== cancelReason || $[67] !== cancelSession) {
    t27 = () => run(() => cancelSession({
      variables: {
        input: {
          advisorSessionId,
          reason: cancelReason.trim() || null
        }
      }
    }), "Could not cancel this session.");
    $[65] = advisorSessionId;
    $[66] = cancelReason;
    $[67] = cancelSession;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  const handleCancel = t27;
  if (!advisorSessionId && router.isReady) {
    let t28;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <SectionScroll><Text color="colorPalette.fg/60">Missing session id.</Text></SectionScroll>;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    return t28;
  }
  let headerAction = null;
  if (canJoin) {
    let t28;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <LuVideo />;
      $[70] = t28;
    } else {
      t28 = $[70];
    }
    let t29;
    if ($[71] !== roomHref) {
      t29 = <Button asChild={true} variant="cta"><NextLink href={roomHref}>{t28} Join session</NextLink></Button>;
      $[71] = roomHref;
      $[72] = t29;
    } else {
      t29 = $[72];
    }
    headerAction = t29;
  } else {
    if (isCompleted && canRate && !myRating) {
      let t28;
      if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = <Button variant="cta" onClick={() => setRateOpen(true)}><LuStar /> Rate session</Button>;
        $[73] = t28;
      } else {
        t28 = $[73];
      }
      headerAction = t28;
    } else {
      if (isCompleted && bookAgainHref) {
        let t28;
        if ($[74] !== bookAgainHref) {
          t28 = <Button asChild={true} variant="cta"><NextLink href={bookAgainHref}>Book again</NextLink></Button>;
          $[74] = bookAgainHref;
          $[75] = t28;
        } else {
          t28 = $[75];
        }
        headerAction = t28;
      } else {
        if (isCompleted && canInviteToBoard) {
          let t28;
          if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
            t28 = <PaywalledAdvisorAction intent="advisor-invite" upgradeCtaLabel="Upgrade to invite"><Button variant="outline" onClick={() => setInviteOpen(true)}>Invite to board</Button></PaywalledAdvisorAction>;
            $[76] = t28;
          } else {
            t28 = $[76];
          }
          headerAction = t28;
        } else {
          if (isCompleted && role === "EXPERT") {
            let t28;
            if ($[77] !== paths.opportunities) {
              t28 = <Button asChild={true} variant="cta"><NextLink href={paths.opportunities}>View opportunities</NextLink></Button>;
              $[77] = paths.opportunities;
              $[78] = t28;
            } else {
              t28 = $[78];
            }
            headerAction = t28;
          }
        }
      }
    }
  }
  const t28 = session?.reason || "Paid video session";
  let t29;
  if ($[79] !== role || $[80] !== session) {
    t29 = session ? `Session with ${personName(role === "EXPERT" ? session.seekerUser : session.expertUser)}` : "Session";
    $[79] = role;
    $[80] = session;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== headerAction || $[83] !== t28 || $[84] !== t29) {
    t30 = <SectionHeader action={headerAction} description={t28} eyebrow={"Advisors \xB7 Sessions"} title={t29} />;
    $[82] = headerAction;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  const t31 = query.loading && !session;
  let t32;
  if ($[86] !== actionError || $[87] !== advisorSessionId || $[88] !== bookAgainHref || $[89] !== canInviteToBoard || $[90] !== cancelReason || $[91] !== cancelState || $[92] !== confirmState || $[93] !== handleCancel || $[94] !== handleConfirm || $[95] !== handlePropose || $[96] !== inboxHref || $[97] !== media || $[98] !== paths.opportunities || $[99] !== paths.sessionSettings || $[100] !== paths.sessions || $[101] !== proposeState || $[102] !== session) {
    t32 = session && <SessionDetailView actionError={actionError} advisorSessionId={advisorSessionId} bookAgainHref={bookAgainHref} cancelLoading={cancelState.loading} cancelReason={cancelReason} canInviteToBoard={canInviteToBoard} confirmLoading={confirmState.loading} inboxHref={inboxHref} media={media} opportunitiesHref={paths.opportunities} proposeLoading={proposeState.loading} session={session} sessionSettingsHref={paths.sessionSettings} sessionsHref={paths.sessions} onCancel={() => void handleCancel()} onCancelReasonChange={setCancelReason} onConfirm={time => void handleConfirm(time)} onInviteToBoard={() => setInviteOpen(true)} onPropose={handlePropose} onRate={() => setRateOpen(true)} />;
    $[86] = actionError;
    $[87] = advisorSessionId;
    $[88] = bookAgainHref;
    $[89] = canInviteToBoard;
    $[90] = cancelReason;
    $[91] = cancelState;
    $[92] = confirmState;
    $[93] = handleCancel;
    $[94] = handleConfirm;
    $[95] = handlePropose;
    $[96] = inboxHref;
    $[97] = media;
    $[98] = paths.opportunities;
    $[99] = paths.sessionSettings;
    $[100] = paths.sessions;
    $[101] = proposeState;
    $[102] = session;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  if ($[104] !== query.error || $[105] !== t31 || $[106] !== t32) {
    t33 = <AdvisorsQueryState error={query.error} loading={t31}>{t32}</AdvisorsQueryState>;
    $[104] = query.error;
    $[105] = t31;
    $[106] = t32;
    $[107] = t33;
  } else {
    t33 = $[107];
  }
  let t34;
  if ($[108] !== counterpartName || $[109] !== query || $[110] !== rateOpen || $[111] !== session) {
    t34 = session && <SessionRatingModal counterpartName={counterpartName} open={rateOpen} session={session} onOpenChange={setRateOpen} onRated={() => void query.refetch()} />;
    $[108] = counterpartName;
    $[109] = query;
    $[110] = rateOpen;
    $[111] = session;
    $[112] = t34;
  } else {
    t34 = $[112];
  }
  let t35;
  if ($[113] !== expertUserId || $[114] !== inviteOpen) {
    t35 = expertUserId && <InviteToBoardModal advisorUserId={expertUserId} isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />;
    $[113] = expertUserId;
    $[114] = inviteOpen;
    $[115] = t35;
  } else {
    t35 = $[115];
  }
  let t36;
  if ($[116] !== t30 || $[117] !== t33 || $[118] !== t34 || $[119] !== t35) {
    t36 = <SectionScroll maxWidth="5xl">{t30}{t33}{t34}{t35}</SectionScroll>;
    $[116] = t30;
    $[117] = t33;
    $[118] = t34;
    $[119] = t35;
    $[120] = t36;
  } else {
    t36 = $[120];
  }
  return t36;
};
function _temp(time) {
  return !!time;
}
function _temp2(open) {
  return !open;
}